import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import ConfirmDialog from 'components/common/ConfirmDialog';
import MasterChartBackButton from 'components/dashboard/healthRecords/MasterChartBackButton';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';
import { masterChartPath } from 'constants/masterChartRoutes';
import { isNkdaList } from 'features/visitStart/visitStartKnownRecord';
import { useMasterChartSetup } from 'components/dashboard/healthRecords/masterChartSetupContext';
import {
  RecordActions,
  RecordIntro,
  RecordCard,
  RecordCardStack,
  joinDetail,
} from 'components/dashboard/healthRecords/RecordList';
// The dialog is shared with the VisitStart check-in, which asks for an allergy
// with the same fields and the same RxNorm lookup.
import AllergyForm, {
  allergyTypeLabel,
} from 'components/individual/health-records/AllergyForm';

/** The page subtitle; in setup, where the subtitle is the MasterChart intro, it sits above Add instead. */
const WHY =
  "Add any allergies you have, to medicines, foods, or anything else, so you're never given something you react to.";

const AllergiesPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const setup = useMasterChartSetup();

  const [allergies, setAllergies] = useState([]);
  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [markingNone, setMarkingNone] = useState(false);

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setLoadError(false);
    try {
      const doc = await db.collection('users').doc(user.uid).get();
      const hr = doc.data()?.healthRecords || {};
      setFullHealthRecords(hr);
      setAllergies(hr.allergies || []);
    } catch {
      // An empty list here would read as "none", and adding to it would replace
      // the stored list, so the list and Add stay hidden until a retry succeeds.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const saveToFirestore = useCallback(
    async (updated) => {
      const merged = {
        ...(fullHealthRecords ?? {}),
        allergies: updated,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
    },
    [user?.uid, fullHealthRecords]
  );

  const openDialog = (index = -1) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  // Rejecting keeps AllergyForm open with the message in place, so a failed write
  // does not silently close the dialog on a half-entered allergy.
  // "No known drug allergies" is stored as a single row (VisitStart and the shared
  // record read it that way) but shown as a statement, not a card. A real
  // allergy retires it: they cannot both be true.
  const noneNoted = isNkdaList(allergies);

  const handleDialogSave = useCallback(
    async (toSave) => {
      const updated = noneNoted ? [] : [...allergies];
      if (editingIndex >= 0) {
        updated[editingIndex] = toSave;
      } else {
        updated.push(toSave);
      }
      updated.sort((a, b) => a.allergen.localeCompare(b.allergen));
      await saveToFirestore(updated);
      setAllergies(updated);
      setSuccessMsg(editingIndex >= 0 ? 'Allergy updated.' : 'Allergy added.');
    },
    [allergies, noneNoted, editingIndex, saveToFirestore]
  );

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const updated = allergies.filter((_, i) => i !== pendingDelete.index);
      setAllergies(updated);
      await saveToFirestore(updated);
      setSuccessMsg(`Removed "${pendingDelete.label}".`);
      setPendingDelete(null);
    } catch {
      setError('Failed to remove. Please try again.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // "None" settles the section, so it saves and moves on: to the next setup
  // step, or back to MasterChart, whose Allergies card then shows done. No
  // message and no card for it here.
  const markNoKnownAllergies = async () => {
    const entry = [{ allergen: 'No Known Drug Allergies', type: 'drug', reaction: '', notes: '' }];
    setMarkingNone(true);
    try {
      await saveToFirestore(entry);
      if (setup) setup.advance();
      else navigate(masterChartPath());
    } catch {
      setError('Failed to save. Please try again.');
      setMarkingNone(false);
    }
  };

  return (
    <div className="inner">
      <DashboardPageHeader
        title={setup?.title ?? 'Allergies'}
        subtitle={setup?.subtitle ?? WHY}
      />

      {loadError && <DataLoadErrorAlert message="We couldn't load your allergies. Please retry before assuming you have none." onRetry={load} />}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {!loadError && (
        <>
          {setup && <RecordIntro text={WHY} />}

          {!loading && noneNoted && (
            <Typography sx={{ mb: 3 }}>You've noted that you have no known drug allergies.</Typography>
          )}

          <RecordCardStack loading={loading}>
            {(noneNoted ? [] : allergies).map((a, i) => (
              <RecordCard
                key={i}
                title={a.allergen}
                detail={joinDetail(allergyTypeLabel(a.type), a.reaction, a.notes)}
                onEdit={() => openDialog(i)}
                onDelete={() => setPendingDelete({ index: i, label: a.allergen })}
                editLabel={`Edit allergy: ${a.allergen}`}
                deleteLabel={`Delete allergy: ${a.allergen}`}
              />
            ))}
          </RecordCardStack>

          <RecordActions
            addLabel="Add an Allergy"
            onAdd={() => openDialog()}
            disabled={loading || markingNone}
            spaced={!loading && allergies.length > 0 && !noneNoted}
          >
            {/* "None" is an answer a doctor needs, and different from a blank list. */}
            {!loading && allergies.length === 0 && (
              <LoadingButton onClick={markNoKnownAllergies} loading={markingNone}>
                No known drug allergies
              </LoadingButton>
            )}
            {!setup && <MasterChartBackButton />}
          </RecordActions>
        </>
      )}

      {setup?.footer}

      <AllergyForm
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingIndex(-1);
        }}
        onSave={handleDialogSave}
        allergy={editingIndex >= 0 ? allergies[editingIndex] : null}
        isEdit={editingIndex >= 0}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Remove Allergy"
        message={`Remove "${pendingDelete?.label}"?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onExited={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default AllergiesPage;
